import React, { useContext, useEffect, useState } from 'react';
import { AppContext } from '../../App';
import Layout from '../../components/Layout';
import { Link } from 'react-router-dom';
import { authAxios } from '../../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { AlertTriangle, UserPlus, Clock, CheckCircle, XCircle, Mail, RefreshCw } from 'lucide-react';

export default function AdminPlayers() {
  const { user, seasons, API, tenantContext } = useContext(AppContext);
  const [users, setUsers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [joinRequests, setJoinRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [seasonFilter, setSeasonFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('players');
  
  // Join Request dialogs
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [denyDialogOpen, setDenyDialogOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [selectedSeasons, setSelectedSeasons] = useState([]);
  const [denyReason, setDenyReason] = useState('');
  const [processingRequest, setProcessingRequest] = useState(false);
  
  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [userToEdit, setUserToEdit] = useState(null);
  const [editName, setEditName] = useState('');
  
  // Impersonate dialog state
  const [impersonateDialogOpen, setImpersonateDialogOpen] = useState(false);
  const [userToImpersonate, setUserToImpersonate] = useState(null);
  
  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  
  // Permanent delete dialog state
  const [permDeleteDialogOpen, setPermDeleteDialogOpen] = useState(false);
  const [permDeleteConfirmOpen, setPermDeleteConfirmOpen] = useState(false);
  const [userToPermDelete, setUserToPermDelete] = useState(null);
  const [permDeleteConfirmText, setPermDeleteConfirmText] = useState('');
  
  // Resend invite state
  const [resendingInvite, setResendingInvite] = useState(null);

  // Check permissions
  const tenantRole = tenantContext?.membership?.role || user?.role;
  const isLeagueAdmin = tenantRole === 'league_admin' || user?.role === 'admin';
  const isModerator = tenantRole === 'moderator';
  const canAccessAdmin = isLeagueAdmin || isModerator;
  const canInvite = isLeagueAdmin; // Only League Admins can invite
  const canDeleteUsers = isLeagueAdmin; // Only League Admins can delete users
  const canManageRequests = isLeagueAdmin || isModerator; // Both can manage join requests

  useEffect(() => {
    if (canAccessAdmin) {
      loadUsers();
      loadJoinRequests();
    }
  }, [user, tenantContext, search, roleFilter, statusFilter, seasonFilter]);

  const loadJoinRequests = async () => {
    try {
      const response = await authAxios.get(`${API}/admin/join-requests?status=all`);
      setJoinRequests(response.data);
    } catch (error) {
      console.error('Failed to load join requests:', error);
    }
  };

  const handleApproveRequest = async () => {
    if (!selectedRequest) return;
    
    try {
      setProcessingRequest(true);
      await authAxios.post(
        `${API}/admin/join-requests/${selectedRequest.id}/approve`,
        { season_ids: selectedSeasons },
        { withCredentials: true }
      );
      toast.success(`${selectedRequest.user_name} has been approved and added to the league!`);
      setApproveDialogOpen(false);
      setSelectedRequest(null);
      setSelectedSeasons([]);
      loadJoinRequests();
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve request');
    } finally {
      setProcessingRequest(false);
    }
  };

  const handleDenyRequest = async () => {
    if (!selectedRequest) return;
    
    try {
      setProcessingRequest(true);
      await authAxios.post(
        `${API}/admin/join-requests/${selectedRequest.id}/deny`,
        { reason: denyReason },
        { withCredentials: true }
      );
      toast.success('Request denied');
      setDenyDialogOpen(false);
      setSelectedRequest(null);
      setDenyReason('');
      loadJoinRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to deny request');
    } finally {
      setProcessingRequest(false);
    }
  };

  const pendingRequests = joinRequests.filter(r => r.status === 'pending');

  const loadUsers = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (roleFilter !== 'all') params.append('role', roleFilter);
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (seasonFilter !== 'all') params.append('season_id', seasonFilter);

      // Load users
      const usersResponse = await authAxios.get(`${API}/users?${params}`);
      setUsers(usersResponse.data);
      
      // Try to load invites, but don't fail if endpoint doesn't exist
      try {
        const invitesResponse = await authAxios.get(`${API}/users/invites`);
        setInvites(invitesResponse.data);
      } catch (inviteError) {
        console.log('Invites endpoint not available:', inviteError);
        setInvites([]);
      }
    } catch (error) {
      console.error('Failed to load users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      active: 'bg-emerald-100 text-emerald-700',
      invited: 'bg-blue-100 text-blue-700',
      deactivated: 'bg-slate-200 text-slate-600'
    };
    return `px-2 py-1 rounded-full text-xs font-medium ${badges[status] || 'bg-slate-100 text-slate-600'}`;
  };

  const getRoleBadges = (userRole, tenantMemberRole) => {
    // Use tenant membership role if available, otherwise fallback to user.role
    const displayRole = tenantMemberRole || userRole;
    
    const roleConfig = {
      league_admin: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'League Admin' },
      admin: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'League Admin' },
      moderator: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Moderator' },
      player: { bg: 'bg-slate-100', text: 'text-slate-600', label: 'Player' }
    };
    
    const config = roleConfig[displayRole] || roleConfig.player;
    return (
      <span className={`${config.bg} ${config.text} px-2 py-1 rounded-full text-xs font-medium`}>
        {config.label}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const handleEditClick = (user, isInvite = false) => {
    setUserToEdit({ ...user, isInvite });
    setEditName(user.name || user.email || '');
    setEditDialogOpen(true);
  };

  const handleEditSave = async () => {
    if (!editName.trim()) {
      toast.error('Name cannot be empty');
      return;
    }

    try {
      if (userToEdit.isInvite) {
        await authAxios.patch(
          `${API}/users/invites/${userToEdit.id}`,
          { name: editName.trim() },
          { withCredentials: true }
        );
      } else {
        await authAxios.patch(
          `${API}/users/${userToEdit.id}`,
          { name: editName.trim() },
          { withCredentials: true }
        );
      }
      toast.success('Player name updated successfully');
      setEditDialogOpen(false);
      setUserToEdit(null);
      setEditName('');
      loadUsers();
    } catch (error) {
      console.error('Failed to update user:', error);
      toast.error(error.response?.data?.detail || 'Failed to update player name');
    }
  };

  const handleDeleteClick = (user, isInvite = false) => {
    setUserToDelete({ ...user, isInvite });
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    setDeleteDialogOpen(false);
    setDeleteConfirmOpen(true);
    setDeleteConfirmText('');
  };

  const handleDeleteFinal = async () => {
    if (deleteConfirmText !== 'DELETE') {
      toast.error('Please type DELETE to confirm');
      return;
    }

    try {
      if (userToDelete.isInvite) {
        await authAxios.delete(
          `${API}/users/invites/${userToDelete.id}`,
          { withCredentials: true }
        );
        toast.success('Invite deleted successfully');
      } else {
        await authAxios.delete(
          `${API}/users/${userToDelete.id}`,
          { withCredentials: true }
        );
        toast.success('Player deleted successfully');
      }
      setDeleteConfirmOpen(false);
      setUserToDelete(null);
      setDeleteConfirmText('');
      loadUsers();
    } catch (error) {
      console.error('Failed to delete user:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete player');
      setDeleteConfirmOpen(false);
    }
  };

  const handleResendInvite = async (invite) => {
    setResendingInvite(invite.id);
    try {
      await authAxios.post(`${API}/users/invites/${invite.id}/resend`);
      toast.success(`Invite resent to ${invite.email}`);
    } catch (error) {
      console.error('Failed to resend invite:', error);
      toast.error(error.response?.data?.detail || 'Failed to resend invite');
    } finally {
      setResendingInvite(null);
    }
  };

  const handleImpersonateClick = (targetUser) => {
    setUserToImpersonate(targetUser);
    setImpersonateDialogOpen(true);
  };

  const confirmImpersonate = async () => {
    if (!userToImpersonate) return;

    try {
      const response = await authAxios.post(
        `${API}/users/${userToImpersonate.id}/impersonate`,
        {},
        { withCredentials: true }
      );
      
      // Store the new session token for the impersonated user
      if (response.data.session_token) {
        localStorage.setItem('session_token', response.data.session_token);
      }
      
      toast.success(`Now logged in as ${userToImpersonate.name}`);
      setImpersonateDialogOpen(false);
      // Reload the page to reflect new session
      setTimeout(() => {
        window.location.href = '/';
      }, 1000);
    } catch (error) {
      console.error('Failed to impersonate user:', error);
      toast.error(error.response?.data?.detail || 'Failed to log in as user');
    }
  };

  const handlePermDeleteClick = (user) => {
    setUserToPermDelete(user);
    setPermDeleteDialogOpen(true);
  };

  const handlePermDeleteConfirm = () => {
    setPermDeleteDialogOpen(false);
    setPermDeleteConfirmOpen(true);
    setPermDeleteConfirmText('');
  };

  const handlePermDeleteFinal = async () => {
    if (permDeleteConfirmText !== 'DELETE') {
      toast.error('Please type DELETE to confirm');
      return;
    }

    try {
      await authAxios.delete(
        `${API}/users/${userToPermDelete.id}/permanent`,
        { withCredentials: true }
      );
      toast.success('Player permanently deleted (retained for auditing)');
      setPermDeleteConfirmOpen(false);
      setUserToPermDelete(null);
      setPermDeleteConfirmText('');
      loadUsers();
    } catch (error) {
      console.error('Failed to permanently delete user:', error);
      toast.error(error.response?.data?.detail || 'Failed to permanently delete player');
      setPermDeleteConfirmOpen(false);
    }
  };

  if (!canAccessAdmin) {
    return (
      <Layout>
        <div className="text-center py-12">
          <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-slate-800 mb-2">Access Denied</h2>
          <p className="text-slate-600">You need admin or moderator privileges to access this page.</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto container-mobile">
        <div className="spacing-section flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="heading-hero text-slate-800 mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
              Players
            </h1>
            <p className="text-responsive text-slate-600">Manage users, roles, and season access</p>
          </div>

          <div className="flex flex-col sm:flex-row gap-2 w-full md:w-auto">
            <Link to="/admin/players/audit" className="w-full sm:w-auto">
              <Button variant="outline" className="w-full">Audit Log</Button>
            </Link>
            {canInvite && (
              <Link to="/admin/players/invite" className="w-full sm:w-auto">
                <Button className="w-full">
                  <UserPlus className="w-4 h-4 mr-2" />
                  Invite Players
                </Button>
              </Link>
            )}
          </div>
        </div>

        {/* Join Requests Alert */}
        {pendingRequests.length > 0 && (
          <Card className="spacing-card bg-amber-50 border-amber-300">
            <CardContent className="padding-card">
              <div className="flex items-center gap-3">
                <Clock className="w-5 h-5 text-amber-600" />
                <div className="flex-1">
                  <p className="font-medium text-amber-900">
                    {pendingRequests.length} pending join request{pendingRequests.length > 1 ? 's' : ''}
                  </p>
                  <p className="text-sm text-amber-700">
                    Review and approve or deny requests from users who want to join your league
                  </p>
                </div>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setActiveTab('requests')}
                  className="border-amber-400 text-amber-700"
                >
                  Review
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="spacing-card">
          <TabsList>
            <TabsTrigger value="players">
              Players ({users.length})
            </TabsTrigger>
            <TabsTrigger value="requests" className="relative">
              Join Requests
              {pendingRequests.length > 0 && (
                <Badge className="ml-2 bg-amber-500">{pendingRequests.length}</Badge>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Players Tab */}
          <TabsContent value="players" className="mt-4">
            {/* Filters */}
            <Card className="mb-4">
              <CardContent className="padding-card">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
                  <Input
                    placeholder="Search by name or email..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />

                  <Select value={roleFilter} onValueChange={setRoleFilter}>
                    <SelectTrigger>
                      <SelectValue placeholder="Role" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Roles</SelectItem>
                      <SelectItem value="league_admin">League Admin</SelectItem>
                      <SelectItem value="moderator">Moderator</SelectItem>
                      <SelectItem value="player">Player</SelectItem>
                    </SelectContent>
                  </Select>

                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger>
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="invited">Invited</SelectItem>
                      <SelectItem value="deactivated">Deactivated</SelectItem>
                    </SelectContent>
                  </Select>

                  <Select value={seasonFilter} onValueChange={setSeasonFilter}>
                    <SelectTrigger>
                      <SelectValue placeholder="Season" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Seasons</SelectItem>
                      {seasons.map(season => (
                        <SelectItem key={season.id} value={season.id}>
                          Season {season.number}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            {/* Users Table */}
            <Card>
              <CardContent className="p-0">
                {loading ? (
                  <div className="text-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600 mx-auto"></div>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="data-table">
                  <thead>
                    <tr>
                      <th>Player ID</th>
                      <th>Photo</th>
                      <th>Player</th>
                      <th>Email</th>
                      <th>Role(s)</th>
                      <th>Status</th>
                      <th>Seasons</th>
                      <th>Last Sign-In</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.length === 0 && invites.length === 0 ? (
                      <tr>
                        <td colSpan={9} className="text-center py-12 text-slate-600">
                          No users found
                        </td>
                      </tr>
                    ) : (
                      <>
                        {users.map(u => (
                          <tr key={u.id} className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="py-4 font-mono text-xs text-slate-500" title={u.id}>
                            {u.id.substring(0, 8)}...
                          </td>
                          <td className="py-4">
                            {u.avatar_url ? (
                              <img
                                src={u.avatar_url}
                                alt={u.name}
                                className="w-10 h-10 rounded-full object-cover"
                              />
                            ) : (
                              <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center text-slate-600 font-bold">
                                {u.name?.charAt(0)?.toUpperCase()}
                              </div>
                            )}
                          </td>
                          <td className="py-4 font-medium">{u.name}</td>
                          <td className="py-4 text-sm text-slate-600">{u.email}</td>
                          <td className="py-4">
                            {getRoleBadges(u.role)}
                          </td>
                          <td className="py-4">
                            <span className={getStatusBadge(u.status)}>
                              {u.status}
                            </span>
                          </td>
                          <td className="py-4">
                            <div className="flex flex-wrap gap-1">
                              {u.season_ids?.length > 0 ? (
                                u.season_ids.slice(0, 3).map(sid => {
                                  const season = seasons.find(s => s.id === sid);
                                  return season ? (
                                    <span key={sid} className="px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs">
                                      S{season.number}
                                    </span>
                                  ) : null;
                                })
                              ) : (
                                <span className="text-xs text-slate-400">None</span>
                              )}
                              {u.season_ids?.length > 3 && (
                                <span className="text-xs text-slate-500">+{u.season_ids.length - 3}</span>
                              )}
                            </div>
                          </td>
                          <td className="py-4 text-sm text-slate-600">
                            {formatDate(u.last_sign_in_at)}
                          </td>
                          <td className="py-4">
                            <div className="flex items-center gap-2">
                              <Link to={`/admin/players/${u.id}`}>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-8 w-8 p-0"
                                  title="View Details"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                  </svg>
                                </Button>
                              </Link>
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-8 w-8 p-0 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                title="Login as User"
                                onClick={() => handleImpersonateClick(u)}
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                </svg>
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-8 w-8 p-0"
                                title="Edit Name"
                                onClick={() => handleEditClick(u)}
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                                title="Delete Player"
                                onClick={() => handleDeleteClick(u)}
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-8 w-8 p-0 text-red-700 hover:text-red-800 hover:bg-red-100 border-red-600"
                                title="Permanently Delete Player"
                                onClick={() => handlePermDeleteClick(u)}
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                </svg>
                              </Button>
                            </div>
                          </td>
                        </tr>
                        ))}
                        
                        {invites.map(invite => (
                          <tr key={invite.id} className="border-b border-slate-100 hover:bg-slate-50 bg-blue-50/30">
                            <td className="py-4 font-mono text-xs text-slate-400">
                              (pending)
                            </td>
                            <td className="py-4">
                              <div className="w-10 h-10 rounded-full bg-blue-200 flex items-center justify-center text-blue-600 font-bold">
                                {invite.email?.charAt(0)?.toUpperCase()}
                              </div>
                            </td>
                            <td className="py-4 font-medium text-slate-500">(Pending)</td>
                            <td className="py-4 text-sm text-slate-600">{invite.email}</td>
                            <td className="py-4">
                              <span className="bg-slate-100 text-slate-600 px-2 py-1 rounded-full text-xs font-medium">
                                {invite.role_default || 'Player'}
                              </span>
                            </td>
                            <td className="py-4">
                              <span className={getStatusBadge('invited')}>
                                invited
                              </span>
                            </td>
                            <td className="py-4">
                              <div className="flex flex-wrap gap-1">
                                {invite.seasons_default?.length > 0 ? (
                                  invite.seasons_default.slice(0, 3).map(sid => {
                                    const season = seasons.find(s => s.id === sid);
                                    return season ? (
                                      <span key={sid} className="px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs">
                                        S{season.number}
                                      </span>
                                    ) : null;
                                  })
                                ) : (
                                  <span className="text-xs text-slate-400">None</span>
                                )}
                                {invite.seasons_default?.length > 3 && (
                                  <span className="text-xs text-slate-500">+{invite.seasons_default.length - 3}</span>
                                )}
                              </div>
                            </td>
                            <td className="py-4 text-sm text-slate-600">
                              {formatDate(invite.created_at)}
                            </td>
                            <td className="py-4">
                              <div className="flex items-center gap-2">
                                <Link to={`/admin/players/${invite.id}`}>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="h-8 w-8 p-0"
                                    title="View Details"
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                    </svg>
                                  </Button>
                                </Link>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-8 w-8 p-0"
                                  title="Edit Name"
                                  onClick={() => handleEditClick(invite, true)}
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                  </svg>
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-8 w-8 p-0 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                  title="Resend Invite"
                                  onClick={() => handleResendInvite(invite)}
                                  disabled={resendingInvite === invite.id}
                                >
                                  {resendingInvite === invite.id ? (
                                    <RefreshCw className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <Mail className="w-4 h-4" />
                                  )}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                                  title="Delete Invite"
                                  onClick={() => handleDeleteClick(invite, true)}
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
          </TabsContent>

          {/* Join Requests Tab */}
          <TabsContent value="requests" className="mt-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <UserPlus className="w-5 h-5" />
                  Join Requests
                </CardTitle>
                <Button variant="outline" size="sm" onClick={loadJoinRequests}>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Refresh
                </Button>
              </CardHeader>
              <CardContent>
                {joinRequests.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <UserPlus className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No join requests</p>
                    <p className="text-sm">Users can request to join your league by searching for your email</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {joinRequests.map(request => (
                      <div 
                        key={request.id} 
                        className={`border rounded-lg p-4 ${
                          request.status === 'pending' ? 'border-amber-300 bg-amber-50' : 
                          request.status === 'approved' ? 'border-emerald-200 bg-emerald-50' :
                          'border-red-200 bg-red-50'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-semibold text-slate-800">{request.user_name}</span>
                              {request.status === 'pending' && (
                                <Badge className="bg-amber-500"><Clock className="w-3 h-3 mr-1" />Pending</Badge>
                              )}
                              {request.status === 'approved' && (
                                <Badge className="bg-emerald-600"><CheckCircle className="w-3 h-3 mr-1" />Approved</Badge>
                              )}
                              {request.status === 'denied' && (
                                <Badge variant="destructive"><XCircle className="w-3 h-3 mr-1" />Denied</Badge>
                              )}
                            </div>
                            <p className="text-sm text-slate-600 flex items-center gap-1">
                              <Mail className="w-3 h-3" />
                              {request.user_email}
                            </p>
                            {request.message && (
                              <p className="text-sm text-slate-600 mt-2 italic">
                                "{request.message}"
                              </p>
                            )}
                            <p className="text-xs text-slate-500 mt-2">
                              Requested: {new Date(request.created_at).toLocaleDateString()} at {new Date(request.created_at).toLocaleTimeString()}
                            </p>
                            {request.reviewed_at && (
                              <p className="text-xs text-slate-500">
                                {request.status === 'approved' ? 'Approved' : 'Denied'}: {new Date(request.reviewed_at).toLocaleDateString()}
                                {request.denial_reason && ` - Reason: ${request.denial_reason}`}
                              </p>
                            )}
                          </div>
                          
                          {request.status === 'pending' && canManageRequests && (
                            <div className="flex gap-2">
                              <Button 
                                size="sm" 
                                className="bg-emerald-600 hover:bg-emerald-700"
                                onClick={() => {
                                  setSelectedRequest(request);
                                  setSelectedSeasons(seasons.map(s => s.id)); // Default: all seasons
                                  setApproveDialogOpen(true);
                                }}
                              >
                                <CheckCircle className="w-4 h-4 mr-1" />
                                Approve
                              </Button>
                              <Button 
                                size="sm" 
                                variant="outline"
                                className="text-red-600 border-red-300 hover:bg-red-50"
                                onClick={() => {
                                  setSelectedRequest(request);
                                  setDenyDialogOpen(true);
                                }}
                              >
                                <XCircle className="w-4 h-4 mr-1" />
                                Deny
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Approve Request Dialog */}
      <Dialog open={approveDialogOpen} onOpenChange={setApproveDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Approve Join Request</DialogTitle>
            <DialogDescription>
              Add {selectedRequest?.user_name} to your league
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="bg-slate-50 rounded-lg p-3">
              <p className="text-sm"><strong>Name:</strong> {selectedRequest?.user_name}</p>
              <p className="text-sm"><strong>Email:</strong> {selectedRequest?.user_email}</p>
              {selectedRequest?.message && (
                <p className="text-sm mt-2 italic">"{selectedRequest?.message}"</p>
              )}
            </div>
            
            <div>
              <Label>Grant access to seasons:</Label>
              <div className="mt-2 space-y-2 max-h-40 overflow-y-auto">
                {seasons.map(season => (
                  <label key={season.id} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedSeasons.includes(season.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedSeasons([...selectedSeasons, season.id]);
                        } else {
                          setSelectedSeasons(selectedSeasons.filter(id => id !== season.id));
                        }
                      }}
                      className="rounded"
                    />
                    <span className="text-sm">Season {season.number}: {season.title}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setApproveDialogOpen(false)}>Cancel</Button>
            <Button 
              className="bg-emerald-600 hover:bg-emerald-700"
              onClick={handleApproveRequest}
              disabled={processingRequest}
            >
              {processingRequest ? 'Approving...' : 'Approve & Add to League'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Deny Request Dialog */}
      <Dialog open={denyDialogOpen} onOpenChange={setDenyDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Deny Join Request</DialogTitle>
            <DialogDescription>
              Deny {selectedRequest?.user_name}'s request to join
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div>
              <Label>Reason (optional)</Label>
              <Textarea
                value={denyReason}
                onChange={(e) => setDenyReason(e.target.value)}
                placeholder="Let them know why their request was denied..."
                className="mt-2"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDenyDialogOpen(false)}>Cancel</Button>
            <Button 
              variant="destructive"
              onClick={handleDenyRequest}
              disabled={processingRequest}
            >
              {processingRequest ? 'Denying...' : 'Deny Request'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Name Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Player Name</DialogTitle>
            <DialogDescription>
              Update the display name for {userToEdit?.email}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="edit-name">Player Name</Label>
              <Input
                id="edit-name"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                placeholder="Enter player name"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleEditSave();
                  }
                }}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleEditSave}>
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog - Step 1 */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Player?</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete <strong>{userToDelete?.name}</strong> ({userToDelete?.email})?
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-slate-600">
              This action will soft-delete the player. They will no longer have access to the system.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteConfirm}>
              Continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog - Step 2 */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600">⚠️ Final Confirmation</DialogTitle>
            <DialogDescription>
              This will permanently delete <strong>{userToDelete?.name}</strong>.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="p-4 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-800 font-medium mb-2">
                Warning: This action cannot be undone!
              </p>
              <p className="text-sm text-red-700">
                The player will be removed from the system and will lose access to all seasons and data.
              </p>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="delete-confirm">
                Type <span className="font-mono font-bold">DELETE</span> to confirm
              </Label>
              <Input
                id="delete-confirm"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder="Type DELETE"
                className="font-mono"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setDeleteConfirmOpen(false);
              setDeleteConfirmText('');
            }}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDeleteFinal}
              disabled={deleteConfirmText !== 'DELETE'}
            >
              Delete Player
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Permanent Delete Confirmation Dialog - Step 1 */}
      <Dialog open={permDeleteDialogOpen} onOpenChange={setPermDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Permanently Delete Player</DialogTitle>
            <DialogDescription>
              This will permanently delete <strong>{userToPermDelete?.name}</strong> ({userToPermDelete?.email}).
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-md">
              <p className="text-sm text-amber-800">
                This player will be hidden from all admin views but their data will be retained in the database for auditing purposes.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPermDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handlePermDeleteConfirm}
            >
              Continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Permanent Delete Confirmation Dialog - Step 2 */}
      <Dialog open={permDeleteConfirmOpen} onOpenChange={setPermDeleteConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-700">⚠️ Final Permanent Delete Confirmation</DialogTitle>
            <DialogDescription>
              This will permanently hide <strong>{userToPermDelete?.name}</strong> from all views.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="p-4 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-800 font-medium mb-2">
                Warning: This action cannot be undone!
              </p>
              <p className="text-sm text-red-700 mb-2">
                The player will be permanently hidden from all admin views. Their data will be retained in the database for auditing.
              </p>
              <p className="text-sm text-red-700">
                This is more restrictive than soft delete - the user will not appear in any lists.
              </p>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="perm-delete-confirm">
                Type <span className="font-mono font-bold">DELETE</span> to confirm
              </Label>
              <Input
                id="perm-delete-confirm"
                value={permDeleteConfirmText}
                onChange={(e) => setPermDeleteConfirmText(e.target.value)}
                placeholder="Type DELETE"
                className="font-mono"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setPermDeleteConfirmOpen(false);
              setPermDeleteConfirmText('');
            }}>
              Cancel
            </Button>
            <Button 
              className="bg-red-700 hover:bg-red-800"
              onClick={handlePermDeleteFinal}
              disabled={permDeleteConfirmText !== 'DELETE'}
            >
              Permanently Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Impersonate Confirmation Dialog */}
      <Dialog open={impersonateDialogOpen} onOpenChange={setImpersonateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Impersonate User</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p>
              Log in as <strong>{userToImpersonate?.name}</strong> ({userToImpersonate?.email})?
            </p>
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-md">
              <p className="text-sm text-blue-800">
                You will be logged in as this user and can access the app from their perspective. You can return to your admin account by logging out and back in.
              </p>
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setImpersonateDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={confirmImpersonate}>
                Impersonate User
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
